/**
 * Associates recorded activities with planned trips by matching the recorded
 * track against the plan's route — and names associated activities per-plan.
 *
 * WHY: the previous matcher only tested the *single first GPS point* against the
 * route within 50 m, at recording start. That missed day-2 of a multi-day trip
 * (camped ~127 m off-route, cold alpine start with no fix yet). This matches on
 * whole-track↔route *overlap*, runs at stop AND lazily when a plan/activity list
 * is viewed (self-healing — no one-off edits), and supports manual override.
 */
import { db } from '../database/schema.js'
import { featureStorage } from '../storage/feature-storage.js'
import { activityStorage } from '../storage/activity-storage.js'
import { planStorage } from '../storage/plan-storage.js'
import { nearestPointOnLine } from '@turf/nearest-point-on-line'
import { lineString, point } from '@turf/helpers'
import type { Plan } from '../schemas/plan.js'
import type { Activity } from '../schemas/activity.js'
import { logger } from '../utils/logger.js'

const log = logger.create('plan-assoc')

/** A track point counts as "on route" within this many metres of the route. */
const MATCH_RADIUS_M = 100
/** Associate when at least this fraction of sampled track points are on-route. */
const MIN_OVERLAP = 0.4
/** Sample every Nth track point to bound cost on long tracks. */
const SAMPLE_STRIDE = 5
/** Bounding-box pre-filter padding (degrees, ~1 km) before the precise test. */
const BBOX_PAD_DEG = 0.01

/** Plan time window: [first time, end of the last day]. null if no times. */
function planWindow(plan: Plan): { start: number; end: number } | null {
  if (!plan.times.length) return null
  const ms = plan.times.map(t => new Date(t.datetime).getTime()).filter(n => !Number.isNaN(n))
  if (!ms.length) return null
  const start = Math.min(...ms)
  const end = new Date(Math.max(...ms))
  end.setHours(23, 59, 59, 999)
  return { start, end: end.getTime() }
}

/** LineString coordinate arrays for a plan's route features. */
async function planRouteLines(planId: string): Promise<number[][][]> {
  const features = await featureStorage.getFeaturesForEntity('plan', planId)
  return features
    .filter(f => f.geometry?.type === 'LineString')
    .map(f => (f.geometry as GeoJSON.LineString).coordinates)
    .filter(coords => coords.length >= 2)
}

function bbox(coords: number[][]): [number, number, number, number] {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity
  for (const [x, y] of coords) {
    if (x < minX) minX = x
    if (x > maxX) maxX = x
    if (y < minY) minY = y
    if (y > maxY) maxY = y
  }
  return [minX, minY, maxX, maxY]
}

function bboxesOverlap(a: number[], b: number[], pad: number): boolean {
  return a[0] <= b[2] + pad && a[2] >= b[0] - pad && a[1] <= b[3] + pad && a[3] >= b[1] - pad
}

/** Fraction of sampled track points within MATCH_RADIUS_M of any route line. */
function overlapFraction(trackCoords: number[][], lines: number[][][]): number {
  if (trackCoords.length < 2 || !lines.length) return 0
  const trackBox = bbox(trackCoords)
  const nearbyLines = lines.filter(line => bboxesOverlap(trackBox, bbox(line), BBOX_PAD_DEG))
  if (!nearbyLines.length) return 0
  const turfLines = nearbyLines.map(line => lineString(line))

  let total = 0
  let on = 0
  for (let i = 0; i < trackCoords.length; i += SAMPLE_STRIDE) {
    const p = point(trackCoords[i])
    total++
    for (const tl of turfLines) {
      // .dist is in kilometres → metres
      if (nearestPointOnLine(tl, p).properties.dist * 1000 <= MATCH_RADIUS_M) {
        on++
        break
      }
    }
  }
  return total > 0 ? on / total : 0
}

/**
 * Best-matching plan for a recorded track, or null. Requires the recording time
 * to fall in the plan window AND sufficient track↔route overlap.
 */
export async function matchPlanForTrack(
  trackCoords: number[][],
  whenMs: number
): Promise<string | null> {
  if (trackCoords.length < 2) return null
  const plans = await db.plans.toArray()
  let bestId: string | null = null
  let bestOverlap = MIN_OVERLAP
  for (const plan of plans) {
    const window = planWindow(plan)
    if (!window || whenMs < window.start || whenMs > window.end) continue
    const lines = await planRouteLines(plan.id)
    if (!lines.length) continue
    const overlap = overlapFraction(trackCoords, lines)
    if (overlap >= bestOverlap) {
      bestId = plan.id
      bestOverlap = overlap
    }
  }
  if (bestId) log.debug(`Matched track to plan ${bestId} (overlap ≥ ${bestOverlap.toFixed(2)})`)
  return bestId
}

/**
 * (Re)name a plan's auto-named activities "Track N of {plan}", numbered by
 * chronological position among ALL the plan's activities. User-named activities
 * keep their position in the count but are never renamed.
 */
export async function renumberPlanTracks(planId: string): Promise<void> {
  const plan = await db.plans.get(planId)
  if (!plan) return
  const ids = await planStorage.getActivityIds(planId)
  const acts = (await Promise.all(ids.map(id => db.activities.get(id)))).filter(
    (a): a is Activity => a !== undefined
  )
  acts.sort((a, b) => (a.startTime ?? '').localeCompare(b.startTime ?? ''))
  for (let i = 0; i < acts.length; i++) {
    const a = acts[i]
    if (a.userNamed) continue
    const name = `Track ${i + 1} of ${plan.name}`
    if (a.name !== name) {
      await activityStorage.updateActivity(a.id, { name })
    }
  }
}

/** Link an activity to a plan (idempotent) and renumber the plan's tracks. */
export async function associateActivityWithPlan(activityId: string, planId: string): Promise<void> {
  await activityStorage.linkToPlan(activityId, planId)
  await renumberPlanTracks(planId)
}

/** Unlink; if the activity was auto-named, clear the name so it reverts to a
 *  geocoded/date name on next view. */
export async function unassociateActivityFromPlan(
  activityId: string,
  planId: string
): Promise<void> {
  await activityStorage.unlinkFromPlan(activityId, planId)
  const activity = await db.activities.get(activityId)
  if (activity && !activity.userNamed) {
    await activityStorage.updateActivity(activityId, { name: undefined })
  }
}

/** Match a just-recorded track to a plan and associate if found. Returns the
 *  plan id, or null if no plan matched. */
export async function autoAssociateActivity(
  activityId: string,
  trackCoords: number[][],
  whenMs: number
): Promise<string | null> {
  // Already linked (e.g. the quick at-start point match): just (re)name it.
  const existing = await activityStorage.getLinkedPlanId(activityId)
  if (existing) {
    await renumberPlanTracks(existing)
    return existing
  }
  const planId = await matchPlanForTrack(trackCoords, whenMs)
  if (!planId) return null
  await associateActivityWithPlan(activityId, planId)
  return planId
}

/**
 * Self-healing pass: link any *unassociated* activities whose track overlaps a
 * plan's route within its time window. Cheap — only touches activities that
 * don't already have a plan. Pass a planId to limit work to one plan's view.
 */
export async function backfillPlanAssociations(onlyPlanId?: string): Promise<number> {
  let linked = 0
  try {
    const activities = await db.activities.toArray()
    for (const a of activities) {
      // Best-effort per activity — a single bad record must not abort the pass.
      try {
        const existing = await activityStorage.getLinkedPlanId(a.id)
        if (existing) continue
        const features = await featureStorage.getFeaturesForEntity('activity', a.id)
        const track = features.find(f => f.type === 'track' && f.geometry?.type === 'LineString')
        if (!track) continue
        const coords = (track.geometry as GeoJSON.LineString).coordinates
        const whenMs = a.startTime ? new Date(a.startTime).getTime() : Date.now()
        const planId = await matchPlanForTrack(coords, whenMs)
        if (!planId) continue
        if (onlyPlanId && planId !== onlyPlanId) continue
        await associateActivityWithPlan(a.id, planId)
        linked++
      } catch (err) {
        log.warn(`Backfill skipped activity ${a.id}:`, err)
      }
    }
  } catch (err) {
    log.warn('Backfill pass failed:', err)
  }
  if (linked > 0) log.debug(`Backfill linked ${linked} activit${linked === 1 ? 'y' : 'ies'}`)
  return linked
}
